import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import parse from "html-react-parser";
import Moment from "moment";
import { toast } from "react-toastify";
import { ActivitiesApi } from "../../services/api/api";
import HelpHint from "../parts/helpHint";

Moment.locale("en");

function Activity(props) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [uniques, setUniques] = useState([]);
  const [sortedActivities, setSortedActivities] = useState({});
  const [selectedActivity, setSelectedActivity] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const isMounted = useRef(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const normalizeActivityTab = (item) => {
    const rawType = item?.activityType || item?.activity?.split(" ")[0] || "Other";
    const map = {
      Collection: "Collections",
      Photoset: "Collections",
      Analyze: "Analysis",
      Analysis: "Analysis",
      Photo: "Media",
      Media: "Media",
    };
    return map[rawType] || rawType;
  };

  const getActivityEndpoint = async () => {
    try {
      const result = await ActivitiesApi.getActivities(props.token);
      
      if (!result.success) {
        toast.error(`Failed to fetch activity logs: ${result.message}`);
        return;
      }

      const responseJson = result.data;

      if (isMounted.current) {
        if (responseJson.length === 0) {
          setMessage("No logs yet.");
        } else {
          const newUniques = [...new Set(responseJson.map((item) => normalizeActivityTab(item)))];
          const newSortedActivities = {};

          responseJson.forEach((item) => {
            const key = normalizeActivityTab(item);
            if (!newSortedActivities[key]) {
              newSortedActivities[key] = [];
            }
            newSortedActivities[key].push(item);
          });

          setActivities(responseJson);
          setUniques(newUniques);
          setSortedActivities(newSortedActivities);

          if (newUniques.length > 0) {
            setSelectedActivity(newSortedActivities[newUniques[0]]);
            setSelectedTab(0);
          }

          // Store with user ID to ensure user-specific caching
          const activityCacheKey = `activity_${props.user?.sub || 'unknown'}`;
          const uniquesCacheKey = `uniqueActivities_${props.user?.sub || 'unknown'}`;
          
          localStorage.setItem(activityCacheKey, JSON.stringify(newSortedActivities));
          localStorage.setItem(uniquesCacheKey, JSON.stringify(newUniques));
        }
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setActivities([]);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    window.scrollTo(0, 0);

    try {
      // Use user-specific cache keys
      const activityCacheKey = `activity_${props.user?.sub || 'unknown'}`;
      const uniquesCacheKey = `uniqueActivities_${props.user?.sub || 'unknown'}`;
      
      const cachedSorted = localStorage.getItem(activityCacheKey);
      const cachedUniques = localStorage.getItem(uniquesCacheKey);

      if (cachedSorted && cachedUniques) {
        const parsedSorted = JSON.parse(cachedSorted);
        const parsedUniques = JSON.parse(cachedUniques);

        if (parsedUniques.length) {
          setSortedActivities(parsedSorted);
          setUniques(parsedUniques);
          setSelectedActivity(parsedSorted[parsedUniques[0]]);
          setLoading(false);
        }
      }
    } catch (e) {
      console.error("Failed to parse activity from localStorage", e);
    }

    getActivityEndpoint();

    return () => {
      isMounted.current = false;
    };
  }, [props.user?.sub]); // Add user dependency to reload on user change

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    const uniqueKey = uniques[newValue];
    setSelectedActivity(sortedActivities[uniqueKey] || []);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: isMobile ? "95vw" : "100%",
      display: 'flex', 
      justifyContent: 'center',
      px: { xs: 1, sm: 2, md: 3 },
      pb: { xs: 10, sm: 6, md: 4 }, // Bottom padding for footer spacing
      mx: "auto",
      boxSizing: "border-box"
    }}>
      <Paper sx={{ 
        width: '100%',
        maxWidth: { xs: '100%', sm: '95%', md: '90%', lg: '85%' },
        mx: 'auto',
        borderRadius: 2
      }} elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", display: 'flex', alignItems: 'center' }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="activity tabs"
            sx={{ px: 2, flex: 1 }}
          >
            {uniques.map((unique) => (
              <Tab label={unique} key={unique} />
            ))}
          </Tabs>
          <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
            <HelpHint
              title="Activity"
              hints={[
                "See a timeline of recent actions across your collections, tiers, and distributions.",
                "Filter activity by type or date to find specific events.",
                "Click an entry to jump to the related collection or distribution.",
              ]}
            />
          </Box>
        </Box>
        <List sx={{ 
          width: '100%', 
          py: 0
        }}>
          {message ? (
            <ListItem sx={{ 
              py: 3,
              justifyContent: 'center'
            }}>
              <ListItemText 
                primary={message} 
                sx={{ textAlign: 'center' }}
                primaryTypographyProps={{
                  variant: 'body1',
                  color: 'text.secondary'
                }}
              />
            </ListItem>
          ) : (
            <>
              {selectedActivity && selectedActivity.length > 0 ? (
                selectedActivity.map((item, index) => (
                  <ListItem 
                    key={index} 
                    divider={index < selectedActivity.length - 1}
                    sx={{ 
                      px: { xs: 2, md: 3 },
                      py: 2,
                      alignItems: 'flex-start'
                    }}
                  >
                    <ListItemText
                      sx={{ width: '100%' }}
                      primary={
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          mb: 1,
                          flexWrap: { xs: 'wrap', sm: 'nowrap' },
                          gap: { xs: 1, sm: 2 }
                        }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              fontWeight: 600,
                              color: 'text.primary',
                              flex: 1,
                              minWidth: 0 // Allows text to shrink if needed
                            }}
                          >
                            {item.activity}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.8rem',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}
                          >
                            {Moment.unix(item.createdAt).format("LLL")}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          component="div"
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.5 }}
                        >
                          {parse(item.remarks)}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem sx={{ 
                  py: 3,
                  justifyContent: 'center'
                }}>
                  <ListItemText 
                    primary="No activities available for this category." 
                    sx={{ textAlign: 'center' }}
                    primaryTypographyProps={{
                      variant: 'body1',
                      color: 'text.secondary'
                    }}
                  />
                </ListItem>
              )}
            </>
          )}
        </List>
      </Paper>
    </Box>
  );
}

export default Activity;
